import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../services/adminApi';

const AdminAuthContext = createContext(null);

/**
 * Provider de autenticação para área administrativa
 * Gerencia login, logout e verificação de permissões
 */
export function AdminAuthProvider({ children }) {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Verifica se há um token válido e se o usuário é admin
   */
  const checkAuth = useCallback(async () => {
    try {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        setLoading(false);
        return false;
      }

      const response = await adminAPI.getMe();
      if (response.status === 'success' && response.data) {
        setAdmin(response.data);
        setError(null);
        return true;
      } else {
        adminAPI.clearToken();
        setAdmin(null);
        return false;
      }
    } catch (err) {
      console.error('Erro ao verificar autenticação:', err);
      adminAPI.clearToken();
      setAdmin(null);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Verificar autenticação ao montar
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  /**
   * Realiza login do administrador
   * @param {string} email - Email do usuário
   * @param {string} password - Senha do usuário
   * @returns {Promise<object>} Dados do admin logado
   */
  const login = async (email, password) => {
    setError(null);
    setLoading(true);

    try {
      // 1. Fazer login normal
      await adminAPI.login(email, password);

      // 2. Verificar se é admin
      const adminResponse = await adminAPI.getMe();
      
      if (adminResponse.status === 'success' && adminResponse.data) {
        setAdmin(adminResponse.data);
        return adminResponse.data;
      } else {
        adminAPI.clearToken();
        throw new Error('Você não possui permissões administrativas.');
      }
    } catch (err) {
      adminAPI.clearToken();
      setAdmin(null);
      const message = err.message || 'Erro ao fazer login';
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Realiza logout do administrador
   */
  const logout = useCallback(() => {
    adminAPI.logout();
    setAdmin(null);
    setError(null);
  }, []);

  /**
   * Verifica se o admin pode editar (admin ou super_admin)
   */
  const canEdit = useCallback(() => {
    return ['admin', 'super_admin'].includes(admin?.role);
  }, [admin]);

  /**
   * Verifica se o admin pode deletar permanentemente (apenas super_admin)
   */
  const canDelete = useCallback(() => {
    return admin?.role === 'super_admin';
  }, [admin]);

  /**
   * Verifica se o admin pode gerenciar outros admins (apenas super_admin)
   */
  const canManageAdmins = useCallback(() => {
    return admin?.role === 'super_admin';
  }, [admin]);

  /**
   * Verifica se o usuário está autenticado como admin
   */
  const isAuthenticated = useCallback(() => {
    return !!admin;
  }, [admin]);

  /**
   * Retorna o nome legível do role
   */
  const getRoleName = useCallback(() => {
    const roles = {
      'super_admin': 'Super Administrador',
      'admin': 'Administrador',
      'viewer': 'Visualizador'
    };
    return roles[admin?.role] || 'Desconhecido';
  }, [admin]);

  const value = {
    admin,
    loading,
    error,
    login,
    logout,
    checkAuth,
    canEdit,
    canDelete,
    canManageAdmins,
    isAuthenticated,
    getRoleName
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
}

/**
 * Hook para usar o contexto de autenticação admin
 * @returns {object} Contexto de autenticação
 */
export function useAdminAuth() {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth deve ser usado dentro de um AdminAuthProvider');
  }
  return context;
}

export default AdminAuthContext;

